import { lazy } from "react";
import { Navigate } from "react-router-dom";
// Re-exported from data/userTypes so existing `from '../routes'` imports keep
// working; utility modules should import from 'data/userTypes' directly.
import { USER_TYPES } from '../data/userTypes';
export { USER_TYPES };

// Every page is a lazy chunk so the initial download is the app shell only
// (Navigation, auth, react-query). A wholesaler never fetches inventory code
// and a tenant never fetches the vendor views; heavy libraries used by a
// single page (Highcharts, the datepicker) ride along in that page's chunk.
// RouteGuard / PublicRouteGuard provide the <Suspense> boundary.
const named = (loader, exportName) =>
  lazy(() => loader().then((m) => ({ default: m[exportName] })));

const InventoryPage = named(() => import("../pages/InventoryPage"), "InventoryPage");
const OverviewPage = named(() => import("../pages/OverviewPage"), "OverviewPage");
const StorePerformancePage = named(() => import("../pages/StorePerformancePage"), "StorePerformancePage");
const UserSettings = named(() => import("../pages/UserSettings"), "UserSettings");
const CreditsPage = named(() => import("../pages/CreditsPage"), "CreditsPage");
const VendorUserSettings = named(() => import("../components/vendorView/VendorSettings"), "VendorSettings");
const VendorCreditsPage = named(() => import("../components/vendorView/CreditsPage"), "CreditsPage");
const CreditMemoPage = named(() => import("../components/credits/memo/CreditMemoPage"), "CreditMemoPage");
const VendorCreditMemoPage = named(() => import("../components/vendorView/credits/memo/CreditMemoPage"), "CreditMemoPage");
const VendorDetailsPage = named(() => import("../components/credits/VendorDetailsPage"), "VendorDetailsPage");
const StoreDetailsPage = named(() => import("../components/vendorView/credits/StoreDetailsPage"), "StoreDetailsPage");
const DiscountDetailsPage = named(() => import("../components/credits/DiscountDetailsPage"), "DiscountDetailsPage");
const AlertsPage = named(() => import("../pages/AlertsPage"), "AlertsPage");
const PartnershipsPage = named(() => import("../pages/PartnershipsPage"), "PartnershipsPage");
const AgreementFormPage = named(() => import("../components/partnerships/AgreementFormPage"), "AgreementFormPage");
const AgreementDetailPage = named(() => import("../components/partnerships/AgreementDetailPage"), "AgreementDetailPage");
const VendorPartnershipsPage = named(() => import("../components/vendorView/partnerships/VendorPartnershipsPage"), "VendorPartnershipsPage");
const UserManagementPage = named(() => import("../pages/UserManagementPage"), "UserManagementPage");

const LoginModal = lazy(() => import("../pages/LoginModal"));
const VendorSignup = lazy(() => import("../pages/VendorSignup"));
const VendorRegistration = lazy(() => import("../pages/VendorRegistration"));
const TenantRegistration = lazy(() => import("../pages/TenantRegistration"));
const ResetPassword = lazy(() => import("../pages/ResetPassword"));
const ForgotPassword = lazy(() => import("../pages/ForgotPassword"));
const CategoryDetailsPage = lazy(() => import("../components/inventory/details/CategoryDetailsPage"));
const SubcategoryDetailsPage = lazy(() => import("../components/inventory/details/SubcategoryDetailsPage"));
const BrandDetailsPage = lazy(() => import("../components/inventory/details/BrandDetailsPage"));
const ProductDetailsPage = lazy(() => import("../components/inventory/details/ProductDetailsPage"));
const GroupDetailsPage = lazy(() => import("../components/inventory/details/GroupDetailsPage"));
const AlertDetailPage = lazy(() => import("../components/alerts/AlertDetailPage"));

// Route permissions
export const ROUTE_PERMISSIONS = {
  PUBLIC: 'public',
  AUTHENTICATED: 'authenticated',
  TENANT: 'tenant',
  WHOLESALER: 'wholesaler',
  // Any user type, but only the 'superadmin' permission group.
  SUPERADMIN: 'superadmin'
};

// User Management is reachable from both portals (a superadmin may be a
// tenant or a wholesaler user); ProtectedRoute checks the permission group.
const userManagementRoute = {
  path: "/user-management",
  element: <UserManagementPage />,
  permission: ROUTE_PERMISSIONS.SUPERADMIN
};

// Invitation links (`/<invite_type>-signup?tk=...`) create a new account and
// finish by signing in as that account. `replacesSession` marks them so App
// ends any existing session instead of bouncing a logged-in user to their
// dashboard, which would drop the invite token (Stables bug #425).
export const publicRoutes = [
  {
    path: "onboard_wholesaler-signup",
    element: <VendorSignup />,
    permission: ROUTE_PERMISSIONS.PUBLIC,
    replacesSession: true
  },
  {
    path: "wholesaler-signup",
    element: <VendorRegistration />,
    permission: ROUTE_PERMISSIONS.PUBLIC,
    replacesSession: true
  },
  {
    path: "tenant-signup",
    element: <TenantRegistration />,
    permission: ROUTE_PERMISSIONS.PUBLIC,
    replacesSession: true
  },
  {
    path: "new-password",
    element: <ResetPassword />,
    permission: ROUTE_PERMISSIONS.PUBLIC
  },
  {
    path: "forgot-password",
    element: <ForgotPassword />,
    permission: ROUTE_PERMISSIONS.PUBLIC
  },
  {
    path: "*", // Catch-all for unauthenticated users
    element: <LoginModal />,
    permission: ROUTE_PERMISSIONS.PUBLIC
  }
];

// True when `pathname` is a public route that must replace the current session
// (see `replacesSession` above). Public route paths are relative to "/".
export const routeReplacesSession = (pathname) => {
  const current = (pathname || "").replace(/^\/+|\/+$/g, "");
  return publicRoutes.some((route) => route.replacesSession && route.path === current);
};

export const tenantRoutes = [
  {
    path: "/",
    element: <Navigate to="/overview" replace />,
    permission: ROUTE_PERMISSIONS.TENANT,
    exact: true
  },
  {
    path: "/overview",
    element: <OverviewPage />,
    permission: ROUTE_PERMISSIONS.TENANT,
    exact: true
  },
  {
    path: "/store-performance",
    element: <StorePerformancePage />,
    permission: ROUTE_PERMISSIONS.TENANT
  },
  {
    path: "/inventory",
    element: <InventoryPage />,
    permission: ROUTE_PERMISSIONS.TENANT
  },
  {
    path: "/inventory/product/:sku",
    element: <ProductDetailsPage />,
    permission: ROUTE_PERMISSIONS.TENANT
  },
  {
    path: "/inventory/category/:name",
    element: <CategoryDetailsPage />,
    permission: ROUTE_PERMISSIONS.TENANT
  },
  {
    path: "/inventory/subcategory/:name",
    element: <SubcategoryDetailsPage />,
    permission: ROUTE_PERMISSIONS.TENANT
  },
  {
    path: "/inventory/brand/:name",
    element: <BrandDetailsPage />,
    permission: ROUTE_PERMISSIONS.TENANT
  },
  {
    path: "/inventory/group/:id",
    element: <GroupDetailsPage />,
    permission: ROUTE_PERMISSIONS.TENANT
  },
  {
    path: "/alerts",
    element: <AlertsPage />,
    permission: ROUTE_PERMISSIONS.TENANT
  },
  {
    path: "/alerts/:alertId",
    element: <AlertDetailPage />,
    permission: ROUTE_PERMISSIONS.TENANT
  },
  {
    path: "/credits",
    element: <CreditsPage />,
    permission: ROUTE_PERMISSIONS.TENANT
  },
  {
    path: "/credits/credit-memo",
    element: <CreditMemoPage />,
    permission: ROUTE_PERMISSIONS.TENANT
  },
  {
    path: "/partnerships",
    element: <PartnershipsPage />,
    permission: ROUTE_PERMISSIONS.TENANT
  },
  {
    path: "/partnerships/new",
    element: <AgreementFormPage />,
    permission: ROUTE_PERMISSIONS.TENANT
  },
  {
    path: "/partnerships/agreements/:agreementId",
    element: <AgreementDetailPage />,
    permission: ROUTE_PERMISSIONS.TENANT
  },
  {
    path: "/partnerships/agreements/:agreementId/edit",
    element: <AgreementFormPage />,
    permission: ROUTE_PERMISSIONS.TENANT
  },
  {
    path: "/vendors/vendor-details",
    element: <VendorDetailsPage />,
    permission: ROUTE_PERMISSIONS.TENANT
  },
  {
    path: "/discounts/discount-details",
    element: <DiscountDetailsPage />,
    permission: ROUTE_PERMISSIONS.TENANT
  },
  {
    path: "/settings",
    element: <UserSettings />,
    permission: ROUTE_PERMISSIONS.TENANT
  },
  userManagementRoute,
  {
    path: "*",
    element: <Navigate to="/overview" replace />,
    permission: ROUTE_PERMISSIONS.TENANT
  }
];

export const wholesalerRoutes = [
  {
    path: "/",
    element: <Navigate to="/credits" replace />,
    permission: ROUTE_PERMISSIONS.WHOLESALER,
    exact: true
  },
  {
    path: "/credits",
    element: <VendorCreditsPage />,
    permission: ROUTE_PERMISSIONS.WHOLESALER
  },
  {
    path: "/credits/credit-memo",
    element: <VendorCreditMemoPage />,
    permission: ROUTE_PERMISSIONS.WHOLESALER
  },
  {
    path: "/stores/store-details",
    element: <StoreDetailsPage />,
    permission: ROUTE_PERMISSIONS.WHOLESALER
  },
  {
    path: "/partnerships",
    element: <VendorPartnershipsPage />,
    permission: ROUTE_PERMISSIONS.WHOLESALER
  },
  {
    path: "/settings",
    element: <VendorUserSettings />,
    permission: ROUTE_PERMISSIONS.WHOLESALER
  },
  userManagementRoute,
  {
    path: "*",
    element: <Navigate to="/credits" replace />,
    permission: ROUTE_PERMISSIONS.WHOLESALER
  }
];

// Get routes based on user type and default for login public route
export const getRoutesByUserType = (userType) => {
  switch (userType) {
    case USER_TYPES.WHOLESALER:
      return wholesalerRoutes;
    case USER_TYPES.TENANT:
        return tenantRoutes;
    default:
      return publicRoutes;
  }
};

// Navigation component config switch for different user types.
// Items flagged `bottom: true` are pinned above the sign-out block, in order
// (User Management for superadmins, then Settings).
export const getNavigationItems = (userType, permissionGroup = null) => {
  const { PageNames } = require("../data/options");
  const { USER_PERMISSIONS } = require("../utils/constants");
  const iconClass = "text-gray-700 dark:text-gray-300";

  // Import icons dynamically to avoid issues
  const { HiOutlineHome, HiOutlineChartBar, HiOutlineClipboardList, HiOutlineCog, HiOutlineTag, HiOutlineBell, HiOutlineOfficeBuilding, HiOutlineUserGroup } = require('react-icons/hi');

  const userManagementItem = { key: 'USER_MANAGEMENT', icon: HiOutlineUserGroup, label: PageNames.USER_MANAGEMENT.name, link: PageNames.USER_MANAGEMENT.link, size: 24, className: iconClass, bottom: true };
  const settingsItem = { key: 'SETTINGS', icon: HiOutlineCog, label: PageNames.SETTINGS.name, link: PageNames.SETTINGS.link, size: 24, className: iconClass, bottom: true };
  const bottomItems = permissionGroup === USER_PERMISSIONS.SUPERADMIN
    ? [userManagementItem, settingsItem]
    : [settingsItem];

  const allMenuItems = [
    { key: 'OVERVIEW', icon: HiOutlineHome, label: PageNames.OVERVIEW.name, link: PageNames.OVERVIEW.link, size: 24, className: iconClass },
    { key: 'PERFORMANCE', icon: HiOutlineChartBar, label: PageNames.PERFORMANCE.name, link: PageNames.PERFORMANCE.link, size: 24, className: iconClass },
    { key: 'INVENTORY', icon: HiOutlineClipboardList, label: PageNames.INVENTORY.name, link: PageNames.INVENTORY.link, size: 24, className: iconClass },
    { key: 'ALERTS', icon: HiOutlineBell, label: PageNames.ALERTS.name, link: PageNames.ALERTS.link, size: 24, className: iconClass },
    { key: 'PROMOTIONS', icon: HiOutlineTag, label: PageNames.PROMOTIONS.name, link: PageNames.PROMOTIONS.link, size: 24, className: iconClass },
    { key: 'PARTNERSHIPS', icon: HiOutlineOfficeBuilding, label: PageNames.PARTNERSHIPS.name, link: PageNames.PARTNERSHIPS.link, size: 24, className: iconClass },
    ...bottomItems,
  ];

  const wholesalerMenuItems = [
    { key: 'PROMOTIONS', icon: HiOutlineTag, label: PageNames.PROMOTIONS.name, link: PageNames.PROMOTIONS.link, size: 24, className: iconClass },
    { key: 'PARTNERSHIPS', icon: HiOutlineOfficeBuilding, label: PageNames.PARTNERSHIPS.name, link: PageNames.PARTNERSHIPS.link, size: 24, className: iconClass },
    ...bottomItems,
  ];

  switch (userType) {
    case USER_TYPES.WHOLESALER:
      return wholesalerMenuItems;
    case USER_TYPES.TENANT:
    default:
      return allMenuItems;
  }
};
